import { timeUnitOf, truncSql, timeKeySql, intervalSql, bucketStartSql } from '@rdrive/plugin-chart-time-series';
import { queryRows } from '@rdrive/data-duckdb';
import { makeFetchLegendTitles } from './statusHistoryLegend';

// Status-history burn-up/burn-down ("how many items sit in each status over
// time") queries for the activity page. SQL only: the series objects, colours,
// legend naming and tooltip markup are presentation and live in @rdrive/plugin-chart-time-series'
// BaseTimeSeriesChart (mode="cumulative").
//
// Deliberately still a near-duplicate of useStatusHistoryVelocityChart: the two
// charts share a shape but not their SQL. What they DID duplicate — the
// date-bucket expressions and `fetchLegendTitles` — is now shared (the helpers
// in `@rdrive/plugin-chart-time-series`, and `./statusHistoryLegend.js`).
//
// This config has no filter context of its own (the page owns it), so it keeps
// plain `queryRows` rather than `makeChartConfig`.
export function useStatusHistoryBurndownChart({ conn }) {
  const run = (query) => queryRows(conn, query);

  const generateBurnupQuery = (groupBy = "day") => {
    /* --- literals that vary with the caller ------------------------ */
    const unit = timeUnitOf(groupBy);
    const interval = intervalSql(unit);

    /* --- full SQL --------------------------------------------------- */
    return `
WITH
/* 1️⃣  +1 / −1 “delta” rows for each status period ------------------ */
deltas AS (
  SELECT subItemId,
         statusId,
         ${truncSql(unit, 'validFrom')}                                  AS bucket,
         +1 AS delta
    FROM filteredStatusHistory
  UNION ALL
  SELECT subItemId,
         statusId,
         ${truncSql(unit, "COALESCE(validTo, CAST('9999-12-31' AS TIMESTAMP))")}     AS bucket,
         -1 AS delta
    FROM filteredStatusHistory
),

/* 2️⃣  aggregate deltas per bucket × colour ------------------------- */
bucket_deltas AS (
  SELECT bucket,
         st.color,
         SUM(delta) AS delta
    FROM deltas d
         JOIN status_templates st ON d.statusId = st.id
   GROUP BY bucket, st.color
),

/* 3️⃣  canonical sort order per colour ------------------------------ */
color_sort_map AS (
  SELECT color, MIN(sort) AS standard_sort
    FROM status_templates
   GROUP BY color
),

/* 4️⃣  calendar buckets from filtered data range ------------------- */
all_buckets AS (
  SELECT ${truncSql(unit, 'gs')} AS bucket                 -- ⭑ use alias gs
    FROM GENERATE_SERIES(
           (SELECT ${truncSql(unit, 'MIN(validFrom)')} FROM filteredStatusHistory),
           (SELECT ${truncSql(unit, 'MAX(COALESCE(validTo, CAST(CURRENT_TIMESTAMP AS TIMESTAMP)))')} FROM filteredStatusHistory),
           ${interval}
         ) AS series(gs)                                      -- ⭑ alias column
),

/* 5️⃣  cross product: every bucket × every colour ------------------- */
all_buckets_and_colors AS (
  SELECT b.bucket,
         c.color,
         c.standard_sort
    FROM all_buckets b
    CROSS JOIN color_sort_map c
),

/* 6️⃣  add real deltas and accumulate running sum ------------------ */
running AS (
  SELECT  abc.bucket,
          abc.color,
          abc.standard_sort,
          COALESCE(bd.delta, 0)                                           AS delta,
          SUM(COALESCE(bd.delta, 0))
              OVER (PARTITION BY abc.color ORDER BY abc.bucket)           AS cumulative_count
    FROM  all_buckets_and_colors abc
          LEFT JOIN bucket_deltas bd
                 ON  abc.bucket = bd.bucket
                 AND abc.color  = bd.color
)

/* 7️⃣  final projection for the chart ------------------------------ */
SELECT
    ${timeKeySql(unit, 'bucket', { isoYear: true })} AS time_key,  -- ISO-year week keys (2026-10-07)
    color as color,
    standard_sort              AS sort,
    cumulative_count           AS count
  FROM running
 ORDER BY bucket, sort;
`;
  };

  // Query for detailed tooltip data. `bucketStartSql` carries the ISO (Jan 4)
  // week anchor this chart already used, and now THROWS on a malformed key — the
  // base chart catches per fetch and renders an empty breakdown.
  const generateTooltipQuery = (timePoint, groupBy = 'day') => {
    /* ---------- derive unit + “bucket start” timestamp ------------- */
    const unit = timeUnitOf(groupBy);
    const boundaryExpr = bucketStartSql(unit, timePoint);

    /* ---------- SQL ------------------------------------------------ */
    return `
WITH
  /* bucket start as TIMESTAMP + its DATE_TRUNC(unit, …) value */
  boundary AS (
    SELECT
      ${boundaryExpr}                           AS ts,
      ${truncSql(unit, boundaryExpr)}    AS bucket
  ),

  /* items that are alive in this bucket --------------------------- */
  status_at_boundary AS (
    SELECT
      sht.subItemId,
      sht.statusId
    FROM   boundary b
    JOIN   filteredStatusHistory sht
           ON  ${truncSql(unit, 'sht.validFrom')} <= b.bucket
           AND (
                 sht.validTo IS NULL
                 OR ${truncSql(unit, 'sht.validTo')} > b.bucket
               )
  ),

  /* aggregated counts by status title / colour -------------------- */
  counts AS (
    SELECT
      st.title,
      st.color,
      MIN(st.sort)              AS sort,
      COUNT(DISTINCT subItemId) AS count
    FROM   status_at_boundary sab
    JOIN   status_templates st  ON sab.statusId = st.id
    GROUP  BY st.title, st.color
  )

/* final projection ------------------------------------------------ */
SELECT
  title as title,
  color as color,
  sort as sort,
  count as count
FROM counts
ORDER BY sort, title;
`;
  };

  // Rows: { timeKey, color (may be null), value, sort } — `value` is the running
  // total the SQL above already accumulates (aliased `count`)
  const fetchSeries = async (groupBy) => {
    const rows = await run(generateBurnupQuery(groupBy));
    return rows.map((row) => ({
      timeKey: row.time_key,
      color: row.color,
      value: Number(row.count),
      sort: Number(row.sort)
    }));
  };

  // Rows: { title, color, sort, count } for one time bucket
  const fetchTooltip = async (timePoint, groupBy) => {
    const rows = await run(generateTooltipQuery(timePoint, groupBy));
    return rows.map((row) => ({
      ...row,
      count: Number(row.count),
      sort: Number(row.sort)
    }));
  };

  // Status titles with activity in the visible buckets, for the exported PNG's legend
  const fetchLegendTitles = makeFetchLegendTitles(conn);

  return { fetchSeries, fetchTooltip, fetchLegendTitles };
}
